import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { ComponentsMeasureEditorConfig, IGroupMeasures, Measures } from './types'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	IStructureAPI,
	Structure,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import { ViewerAPI, SiteMeasureMap } from 'thunderbolt-viewer-manager-types'
import { name } from './symbols'
import { Component } from '@wix/thunderbolt-becky-types'
import { getBoundingLayout } from './boundingLayout'

type GroupsChildrenMeasures = {
	[groupId: string]: { [compId: string]: Pick<Measures, 'left' | 'top' | 'contentHeight'> }
}

const groupMeasuresFactory = (
	structureViewerApi: ViewerAPI['structure'],
	window: NonNullable<BrowserWindow>,
	config: ComponentsMeasureEditorConfig,
	structureStoreAPI: IStructureAPI
): IGroupMeasures => {
	const groupsChildrenMeasures: GroupsChildrenMeasures = {}
	let isGroupCustomMeasuresDisabled = false

	const calcChildrenBoundingBorder = (groupId: string, children: Array<Component>, measureMap: SiteMeasureMap) => {
		let mostLeft: number, mostRight: number, mostTop: number, mostBottom: number
		mostLeft = mostTop = Number.MAX_VALUE
		mostRight = mostBottom = -Number.MAX_VALUE

		isGroupCustomMeasuresDisabled = true

		children.forEach((child) => {
			const { id } = child
			const childLeft = measureMap.left[id]
			const childWidth = measureMap.width[id]
			const childTop = measureMap.top[id]
			const childHeight = measureMap.height[id]
			const childContentHeight = measureMap.contentHeight[id]

			groupsChildrenMeasures[groupId][id] = { top: childTop, left: childLeft, contentHeight: childContentHeight }

			const childBoundingLayout = getBoundingLayout({
				x: childLeft,
				y: childTop,
				width: childWidth,
				height: childHeight,
				rotationInDegrees: child.layout.rotationInDegrees,
			})

			mostLeft = Math.min(mostLeft, childBoundingLayout.x)
			mostRight = Math.max(mostRight, childBoundingLayout.x + childBoundingLayout.width)
			mostTop = Math.min(mostTop, childBoundingLayout.y)
			mostBottom = Math.max(mostBottom, childBoundingLayout.y + childBoundingLayout.height)
		})

		isGroupCustomMeasuresDisabled = false

		return { left: mostLeft, right: mostRight, top: mostTop, bottom: mostBottom }
	}

	const isGroupComponent: IGroupMeasures['isGroupComponent'] = (compId) => {
		const compType = structureStoreAPI.get(compId)?.componentType
		return compType === 'Group'
	}

	const isGroupChild: IGroupMeasures['isGroupChild'] = (compId) => {
		const compParent = structureViewerApi.getStructure(compId)?.parent
		if (!compParent) {
			return false
		}

		return isGroupComponent(compParent)
	}

	const getMeasuresForGroupChild: IGroupMeasures['getMeasuresForGroupChild'] = (id, measureMap) => {
		const groupId = structureViewerApi.getStructure(id)!.parent!
		const groupChildrenMeasures = groupsChildrenMeasures[groupId]

		if (groupChildrenMeasures && groupChildrenMeasures[id]) {
			return groupChildrenMeasures[id]
		}

		isGroupCustomMeasuresDisabled = true

		const groupChildMeasures: Pick<Measures, 'left' | 'top' | 'contentHeight'> = {
			top: measureMap.top[id],
			left: measureMap.left[id],
			contentHeight: measureMap.contentHeight[id],
		}

		isGroupCustomMeasuresDisabled = false

		return groupChildMeasures
	}

	const getMeasuresForGroup: IGroupMeasures['getMeasuresForGroup'] = (groupId, measureMap, measuresApi) => {
		const groupMeasures: Partial<Measures> = {}
		groupsChildrenMeasures[groupId] = {}

		const { components: groupChildren, layout } = structureViewerApi.getStructure(groupId)!

		const groupElement = measuresApi.getCompElement(groupId)!
		const groupRect = groupElement.getBoundingClientRect()

		groupMeasures.left = groupRect.left

		if (groupChildren && groupChildren.length > 0) {
			const childrenStructure = groupChildren.map((childId) => structureViewerApi.getStructure(childId))
			const childrenBorder = calcChildrenBoundingBorder(groupId, childrenStructure, measureMap)

			const groupTop = measuresApi.getCompTop(groupElement, groupId, measureMap) + childrenBorder.top
			const groupLeft = measuresApi.getCompLeft(groupElement, groupId, measureMap) + childrenBorder.left

			const structureX = layout.x || 0
			const structureY = layout.y || 0

			groupMeasures.width = childrenBorder.right - childrenBorder.left
			groupMeasures.height = childrenBorder.bottom - childrenBorder.top
			groupMeasures.top = Math.abs(structureY - groupTop) <= 0.5 ? structureY : groupTop
			groupMeasures.left = Math.abs(structureX - groupLeft) <= 0.5 ? structureX : groupLeft

			childrenStructure.forEach((child) => {
				const { id } = child
				groupsChildrenMeasures[groupId][id].top -= childrenBorder.top
				groupsChildrenMeasures[groupId][id].left -= childrenBorder.left
			})
		}

		return groupMeasures as Measures
	}

	const groupMeasureTypes = ['left', 'top', 'width', 'height']

	return {
		isGroupComponent,
		isGroupChild,
		shouldUseCustomMeasureForGroupChild: (compId: string) => !isGroupCustomMeasuresDisabled && isGroupChild(compId),
		shouldUseCustomMeasureForGroup: (compId: string, measureType: string) =>
			!isGroupCustomMeasuresDisabled && isGroupComponent(compId) && groupMeasureTypes.includes(measureType),
		getMeasuresForGroup,
		getMeasuresForGroupChild,
	}
}

export const GroupMeasures = withDependencies(
	[ViewerManagerStructureAPISymbol, BrowserWindowSymbol, named(EditorFeatureConfigSymbol, name), Structure],
	groupMeasuresFactory
)
